import { taggedTemplateLiteral as _taggedTemplateLiteral } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { bool, oneOfType, arrayOf, node, oneOf } from 'prop-types';
import styled, { css } from 'styled-components';
import theme from 'styled-theming';
import shade from 'polished/lib/color/shade';
import transparentize from 'polished/lib/color/transparentize';
import { Label } from '../form/formstyles.js';
import 'react';
import { stretchClickableStyles } from '../../utils/styleHelpers.js';
import { CARD_VARIANTS, CARD_DENSITIES } from './constants.js';
export { CARD_DENSITIES, CARD_VARIANTS } from './constants.js';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14, _templateObject15, _templateObject16, _templateObject17, _templateObject18, _templateObject19;

/**
 * Default container for when multiple cards are displayed
 * (e.g. lists, grid, tables, etc) where they need to match
 * the height of the siblings.
 */
const CardWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "card__CardWrapper",
  componentId: "sc-hbvqm8-0"
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: column;\n  height: 100%;\n  flex-grow: 1;\n\n  [data-card-button]:hover {\n    color: ", ";\n    background: ", ";\n  }\n"])), _ref => {
  let {
    theme,
    premium
  } = _ref;
  return premium ? theme.colors.surface : theme.colors.text;
}, _ref2 => {
  let {
    theme,
    premium
  } = _ref2;
  return transparentize(0.8, premium ? theme.colors.surface : theme.colors.accents.dark);
});

/**
 * Container based in CardWrapper, with extended styles for
 * Cards that are selectable as well as when currently selected.
 */
const SelectableCardWrapper = /*#__PURE__*/styled(CardWrapper).withConfig({
  displayName: "card__SelectableCardWrapper",
  componentId: "sc-hbvqm8-1"
})(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  &:focus-within {\n    > div {\n      border-color: ", ";\n    }\n  }\n\n  [type='radio'],\n  [type='checkbox'] {\n    &:checked + * {\n      background-color: ", ";\n      box-shadow: ", ";\n      border-color: ", ";\n\n      label {\n        color: ", ";\n      }\n\n      [data-card-description] {\n        ", ";\n      }\n      [data-card-ribbon] {\n        ", "\n      }\n\n      [data-card-button] {\n        background-color: ", ";\n        border-color: ", ";\n        color: ", ";\n      }\n    }\n\n    &:not(:checked):hover + * {\n      [data-card-button] {\n        color: ", ";\n        background: ", ";\n      }\n    }\n  }\n"])), _ref3 => {
  let {
    theme
  } = _ref3;
  return theme.colors.primary.dark;
}, _ref4 => {
  let {
    theme
  } = _ref4;
  return theme.colors.primary.light;
}, _ref5 => {
  let {
    theme
  } = _ref5;
  return theme.boxShadows.default;
}, _ref6 => {
  let {
    theme
  } = _ref6;
  return theme.colors.primary.dark;
}, _ref7 => {
  let {
    theme
  } = _ref7;
  return theme.colors.primary.contrastTextLight;
}, _ref8 => {
  let {
    theme: {
      colors
    }
  } = _ref8;
  return theme('name', {
    pureGym: "color: ".concat(colors.primary.contrastTextLight, ";")
  });
}, _ref9 => {
  let {
    theme: {
      colors
    }
  } = _ref9;
  return theme('name', {
    pureGym: "\n              background-color: ".concat(shade(0.3, colors.primary.main), ";\n              color: ").concat(colors.primary.contrastText, ";")
  });
}, _ref10 => {
  let {
    theme
  } = _ref10;
  return theme.colors.primary.dark;
}, _ref11 => {
  let {
    theme
  } = _ref11;
  return theme.colors.primary.dark;
}, _ref12 => {
  let {
    theme
  } = _ref12;
  return theme.colors.primary.contrastText;
}, _ref13 => {
  let {
    theme,
    premium
  } = _ref13;
  return premium ? theme.colors.surface : theme.colors.text;
}, _ref14 => {
  let {
    theme,
    premium
  } = _ref14;
  return transparentize(0.8, premium ? theme.colors.surface : theme.colors.accents.dark);
});

/**
 * Default card heading styles, for consistency of both heading and label,
 * while having in consideration the Card variant where they're used
 */
const defaultCardHeadingsStyles = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  margin-block: 0;\n  line-height: 1;\n  color: ", ";\n"])), _ref15 => {
  let {
    theme: {
      colors
    },
    variant
  } = _ref15;
  return variant === CARD_VARIANTS.PREMIUM && theme('name', {
    pureGym: css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n        ", ";\n      "])), colors.brandAccents.lightTeal)
  });
});

// Default card styles and style-helpers
const CardLabel = /*#__PURE__*/styled(Label).withConfig({
  displayName: "card__CardLabel",
  componentId: "sc-hbvqm8-2"
})(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n  font-size: ", ";\n  font-family: ", ";\n  text-transform: uppercase;\n  ", "\n\n  [dir='rtl'] & {\n    font-family: ", ";\n    line-height: ", ";\n    font-weight: bold;\n    text-transform: none;\n  }\n"])), _ref16 => {
  let {
    theme
  } = _ref16;
  return theme.fontSizes.xxlarge;
}, _ref17 => {
  let {
    theme
  } = _ref17;
  return theme.fonts.heading;
}, stretchClickableStyles, _ref18 => {
  let {
    theme
  } = _ref18;
  return theme.rtlFonts.heading;
}, _ref19 => {
  let {
    theme
  } = _ref19;
  return theme.rtlLineHeights.heading;
});
const Card = /*#__PURE__*/styled.div.withConfig({
  displayName: "card__Card",
  componentId: "sc-hbvqm8-3"
})(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n  position: relative;\n  display: flex;\n  flex-direction: column;\n  align-items: flex-start;\n  padding: ", ";\n  flex-grow: 1;\n\n  border: ", ";\n  border-radius: ", ";\n  background-color: ", ";\n  transition: ", ";\n  box-shadow: ", ";\n\n  h1,\n  h2,\n  h3,\n  h4,\n  h5,\n  h6,\n  ", " {\n    color: ", ";\n    ", "\n  }\n\n  p {\n    margin-block: 0;\n  }\n\n  ", "\n\n  ", "\n"])), _ref20 => {
  let {
    theme
  } = _ref20;
  return theme.spacing.baseSpacing;
}, _ref21 => {
  let {
    theme
  } = _ref21;
  return "1px solid ".concat(theme.colors.opacities.invertedSubtle);
}, _ref22 => {
  let {
    theme
  } = _ref22;
  return theme.radii.default;
}, _ref23 => {
  let {
    theme
  } = _ref23;
  return theme.colors.background;
}, _ref24 => {
  let {
    theme
  } = _ref24;
  return theme.transitionsMs.default;
}, _ref25 => {
  let {
    theme
  } = _ref25;
  return theme.boxShadows.default;
}, CardLabel, _ref26 => {
  let {
    theme
  } = _ref26;
  return theme.colors.primary.main;
}, defaultCardHeadingsStyles, _ref27 => {
  let {
    theme: {
      mediaQueries
    },
    density
  } = _ref27;
  switch (density) {
    case CARD_DENSITIES.DEFAULT:
      return css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n          gap: ", ";\n\n          ", " {\n            gap: ", ";\n          }\n        "])), _ref28 => {
        let {
          theme
        } = _ref28;
        return theme.spacing.halfSpacing;
      }, mediaQueries.md, _ref29 => {
        let {
          theme
        } = _ref29;
        return theme.spacing.baseSpacing;
      });
    case CARD_DENSITIES.COMFORTABLE:
      return css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n          gap: ", ";\n\n          ", " {\n            gap: ", ";\n          }\n        "])), _ref30 => {
        let {
          theme
        } = _ref30;
        return theme.spacing.baseSpacing;
      }, mediaQueries.md, _ref31 => {
        let {
          theme
        } = _ref31;
        return theme.spacing.threeQuarterSpacing;
      });
    case CARD_DENSITIES.COMPACT:
      return css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n          gap: ", ";\n\n          ", " {\n            gap: ", ";\n          }\n        "])), _ref32 => {
        let {
          theme
        } = _ref32;
        return theme.spacing.quarterSpacing;
      }, mediaQueries.md, _ref33 => {
        let {
          theme
        } = _ref33;
        return theme.spacing.halfSpacing;
      });
    case CARD_DENSITIES.COLLAPSE:
      return css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n          gap: 0;\n        "])));
    default:
      return css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n          gap: ", ";\n\n          ", " {\n            gap: ", ";\n          }\n        "])), _ref34 => {
        let {
          theme
        } = _ref34;
        return theme.spacing.halfSpacing;
      }, mediaQueries.md, _ref35 => {
        let {
          theme
        } = _ref35;
        return theme.spacing.baseSpacing;
      });
  }
}, _ref36 => {
  let {
    theme: {
      colors
    },
    variant
  } = _ref36;
  switch (variant) {
    case CARD_VARIANTS.STANDARD:
      return css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n          background-color: ", ";\n          border-color: ", ";\n        "])), colors.surface, colors.accents.muted);
    case CARD_VARIANTS.PLAIN:
      return css(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n          background-color: ", ";\n          border-color: ", ";\n        "])), colors.background, colors.opacities.invertedSubtle);
    case CARD_VARIANTS.PREMIUM:
      return css(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n          background-color: ", ";\n          border-color: ", ";\n          color: ", ";\n        "])), colors.brandAccents.purple, colors.brandAccents.purple, colors.primary.contrastText);
    default:
      return css(_templateObject15 || (_templateObject15 = _taggedTemplateLiteral(["\n          background-color: ", ";\n          border-color: ", ";\n        "])), colors.surface, colors.accents.muted);
  }
});
const CardFooter = /*#__PURE__*/styled.div.withConfig({
  displayName: "card__CardFooter",
  componentId: "sc-hbvqm8-4"
})(_templateObject16 || (_templateObject16 = _taggedTemplateLiteral(["\n  display: flex;\n  justify-content: flex-end;\n  width: 100%;\n"])));

// Table specific Card styles and style-helpers
const CardTable = /*#__PURE__*/styled(Card).withConfig({
  displayName: "card__CardTable",
  componentId: "sc-hbvqm8-5"
})(_templateObject17 || (_templateObject17 = _taggedTemplateLiteral(["\n  align-items: center;\n  text-align: center;\n  min-height: 150px;\n  padding: ", ";\n  h1,\n  h2,\n  h3,\n  h4,\n  h5,\n  h6,\n  ", " {\n    display: inline;\n    color: ", ";\n    ", "\n  }\n"])), _ref37 => {
  let {
    theme
  } = _ref37;
  return "".concat(theme.spacing.baseSpacing, " ").concat(theme.spacing.halfSpacing);
}, CardLabel, _ref38 => {
  let {
    theme
  } = _ref38;
  return theme.colors.text;
}, defaultCardHeadingsStyles);
const CardTableDescription = /*#__PURE__*/styled.p.withConfig({
  displayName: "card__CardTableDescription",
  componentId: "sc-hbvqm8-6"
})(_templateObject18 || (_templateObject18 = _taggedTemplateLiteral(["\n  line-height: 1.2;\n  [dir='rtl'] & {\n    line-height: 1.2;\n  }\n  margin-block-end: 0;\n  font-weight: normal;\n  font-size: ", ";\n  ", " {\n    font-size: ", ";\n  }\n"])), _ref39 => {
  let {
    theme
  } = _ref39;
  return theme.fontSizes.xsmall;
}, _ref40 => {
  let {
    theme
  } = _ref40;
  return theme.mediaQueries.lg;
}, _ref41 => {
  let {
    theme
  } = _ref41;
  return theme.fontSizes.small;
});
const CardRibbon = /*#__PURE__*/styled.div.withConfig({
  displayName: "card__CardRibbon",
  componentId: "sc-hbvqm8-7"
})(_templateObject19 || (_templateObject19 = _taggedTemplateLiteral(["\n  margin-block: ", ";\n  padding: ", ";\n  // baseSpacing for padding (12px) + border on each side (1px)\n  width: calc(100% + 14px);\n  box-shadow: 4px 0 5px -4px rgb(0 0 0 / 80%), -4px 0 5px -4px rgba(0 0 0 / 80%);\n\n  font-family: ", ";\n  font-size: ", ";\n  text-transform: uppercase;\n\n  [dir='rtl'] & {\n    font-family: ", ";\n    font-style: normal;\n    font-weight: bold;\n    text-transform: none;\n  }\n\n  background-color: ", ";\n  color: ", ";\n\n  ", ";\n\n  ", " {\n    font-size: ", ";\n  }\n"])), _ref42 => {
  let {
    theme
  } = _ref42;
  return theme.spacing.halfSpacing;
}, _ref43 => {
  let {
    theme
  } = _ref43;
  return "".concat(theme.spacing.halfSpacing, " ").concat(theme.spacing.baseSpacing);
}, _ref44 => {
  let {
    theme
  } = _ref44;
  return theme.fonts.heading;
}, _ref45 => {
  let {
    theme
  } = _ref45;
  return theme.fontSizes.xxsmall;
}, _ref46 => {
  let {
    theme
  } = _ref46;
  return theme.rtlFonts.heading;
}, _ref47 => {
  let {
    theme
  } = _ref47;
  return theme.colors.primary.main;
}, _ref48 => {
  let {
    theme
  } = _ref48;
  return theme.colors.primary.contrastText;
}, _ref49 => {
  let {
    theme: {
      colors
    }
  } = _ref49;
  return theme('name', {
    pureGym: "\n        background-color: ".concat(colors.brandAccents.lightTeal, ";\n        color: ").concat(colors.primary.contrastTextLight, ";\n      ")
  });
}, _ref50 => {
  let {
    theme
  } = _ref50;
  return theme.mediaQueries.lg;
}, _ref51 => {
  let {
    theme
  } = _ref51;
  return theme.fontSizes.xsmall;
});
CardWrapper.propTypes = {
  premium: bool
};
SelectableCardWrapper.propTypes = {
  premium: bool
};
Card.propTypes = {
  children: oneOfType([arrayOf(node), node]).isRequired,
  density: oneOf(Object.values(CARD_DENSITIES)),
  variant: oneOf(Object.values(CARD_VARIANTS))
};
CardFooter.propTypes = {
  children: oneOfType([arrayOf(node), node]).isRequired
};

export { Card, CardFooter, CardLabel, CardRibbon, CardTable, CardTableDescription, CardWrapper, SelectableCardWrapper };
